import { getAttrMarkup, IBuildTaskDef, register, getFormMarkupAttr } from '@wm/transpiler';
import { IDGenerator, isDefined, FormWidgetType, isMobileApp, getFormWidgetTemplate, getRequiredFormWidget } from '@wm/core';

const tagName = 'div';
const idGen = new IDGenerator('formfield_');
const ALLFIELDS = 'All Fields';

const DATASET_WIDGETS: Set<string> = new Set([FormWidgetType.SELECT, FormWidgetType.CHECKBOXSET, FormWidgetType.RADIOSET,
  FormWidgetType.SWITCH, FormWidgetType.AUTOCOMPLETE, FormWidgetType.CHIPS, FormWidgetType.TYPEAHEAD, FormWidgetType.RATING]);


export const isDataSetWidget = (widget: string) => {
  return DATASET_WIDGETS.has(widget);
};

const getCaptionByWidget = (attrs: Map<string, any>, widgetType: string, counter: any) => {
  if (attrs.get('is-related') === 'true') {
    return `${counter}.getDisplayExpr()`;
  }
  if (widgetType === FormWidgetType.PASSWORD) {
    return '\'********\'';
  }
  let caption = `${counter}.value`;
  if (widgetType === FormWidgetType.DATETIME || widgetType === FormWidgetType.TIMESTAMP) {
    caption += ` | toDate:${counter}.formWidget.datepattern || 'yyyy-MM-dd hh:mm:ss a'`;
    return caption;
  }
  if (widgetType === FormWidgetType.TIME) {
    caption += ` | toDate:${counter}.formWidget.timepattern || 'hh:mm a'`;
    return caption;
  }
  if (widgetType === FormWidgetType.DATE) {
    caption += ` | toDate:${counter}.formWidget.datepattern ||  'yyyy-MMM-dd'`;
    return caption;
  }
  if (widgetType === FormWidgetType.RATING || widgetType === FormWidgetType.UPLOAD) {
    return '';
  }
  if (isDataSetWidget(widgetType) && attrs.get('datafield') === ALLFIELDS) {
    return `${counter}.getDisplayExpr()`;
  }
  return `${counter}.datavalue || ''`;
};

const getWidgetTemplate = (attrs: Map<string, any>, options: any) => {
  let customAttrs = ``;
  if(options.widgetType === "custom-widget") {
    for (let [key, value] of attrs) {
      if(key.startsWith('prop-'))
        customAttrs += key + '="' + value + '" ';
    }
  }
  const name = attrs.get('name');
  const customWidgetName = attrs.get('widgetname');
  const fieldName = (attrs.get('key') || name || '').trim();
  const formControl = options.isMaxWidget ? `formControlName="${fieldName}_max"` : (options.isInList ? `[formControlName]="${options.counter}._fieldName"` : `formControlName="${fieldName}"`);
  const tmplRef = options.isMaxWidget ? `#formWidgetMax` : `#formWidget`;
  const widgetName = name ? (options.isMaxWidget ? `name="${name}_formWidgetMax"` : `name="${name}_formWidget"`) : '';
  // const conditionalClass = `[ngClass]="${attrs.get('ngclass')}"`;
  // TODO: remove this (add if required) [class.hidden]="!${options.pCounter}.isUpdateMode && ${options.counter}.viewmodewidget !== 'default'"
  const defaultTmpl = ` [class.hidden]="!${options.pCounter}?.updateMode && ${options.counter}.viewmodewidget !== 'default'" ${formControl} ${options.eventsTmpl} ${tmplRef} ${widgetName} ${customWidgetName ? `widgetname=${customWidgetName} ${customAttrs}`:''}`;
  return getFormWidgetTemplate(options.widgetType, defaultTmpl, attrs, {counter: options.counter, pCounter: options.pCounter});
};

const getTemplate = (attrs: Map<string, any>, widgetType: string, eventsTmpl: any, counter: any, pCounter: any, isInList: boolean) => {
  const isRange = attrs.get('is-range') === 'true';
  if (!isRange) {
    return getWidgetTemplate(attrs, {widgetType, eventsTmpl, counter, pCounter, isInList});
  }
  const layoutClass = isMobileApp() ? 'col-xs-6' : 'col-sm-6';
  return `<div class="${layoutClass}">${getWidgetTemplate(attrs, {widgetType, eventsTmpl, counter, pCounter})}</div>
                <div class="${layoutClass}">${getWidgetTemplate(attrs, {widgetType, eventsTmpl, counter, pCounter, isMaxWidget: true})}</div>`;
};

const registerFormField = (isFormField:any): IBuildTaskDef => {
    return {
        requires: ['wm-form', 'wm-liveform', 'wm-livefilter', 'wm-list'],
        pre: (attrs, shared, parentForm, parentLiveForm, parentFilter, parentList) => {
          const counter = idGen.nextUid();
          const dataRole = 'form-field';
          const widgetType = attrs.get('widget') || attrs.get('displaywidget') || FormWidgetType.TEXT;
          const controlLayout: string = isMobileApp() ? 'col-xs-12' : 'col-sm-12';
          const parent = parentForm || parentLiveForm || parentFilter;
          const pCounter = (parent && parent.get('form_reference')) || 'form';
          const isInList = pCounter === (parentList && parentList.get('parent_form_reference'));
          return `<${tagName} data-role="${dataRole}" wmFormField wmCaptionPosition #${counter}="wmFormField" widgettype="${widgetType}" ${getFormMarkupAttr(attrs)}>
                    <div class="live-field form-group app-composite-widget clearfix caption-{{captionposition}}" widget="${widgetType}">
                        <label [hidden]="!${counter}.displayname && !${counter}.binddisplayname" class="app-label control-label formfield-label"
                              [ngStyle]="{width: ${pCounter}?.captionsize}" [ngClass]="{'text-danger': ${counter}._control?.invalid && ${counter}._control?.touched && ${pCounter}?.isUpdateMode,
                                         required: ${pCounter}?.isUpdateMode && ${counter}.required}" [textContent]="${counter}.displayname || ${counter}.binddisplayname"> </label>
                            <div class="${counter}.displayname ? ${pCounter}?._widgetClass : ${controlLayout}">
                                 <label class="form-control-static app-label"
                                  [class.hidden]="(!(!${counter}.form?.updateMode && ${counter}.$attrs.get('viewmodewidget') !== 'default')) || true"
                                       [innerHTML]="${getCaptionByWidget(attrs, widgetType, counter)}"></label>
                                ${getTemplate(attrs, widgetType, '', counter, pCounter, isInList)}
                                <span aria-hidden="true" *ngIf="${counter}.showPendingSpinner" class="form-field-spinner fa fa-circle-o-notch fa-spin form-control-feedback"></span>
                                <p [hidden]="(${counter}._control?.invalid && ${counter}._control?.touched) && !${pCounter}?.updateMode"
                                   class="help-block" aria-hidden="true" role="alert" aria-live="polite" [textContent]="${counter}.bindhint || ${counter}.hint"></p>

                            </div>
                        </div>`
        },
        post: () => `</${tagName}>`,
        provide: (attrs, shared) => {
          const provider = new Map();
          provider.set('form_reference', shared?.get('counter'));
          return provider;
        },
        imports: (attrs: Map<String, String>): string[] => {
          const requiredWidget = getRequiredFormWidget(attrs.get('__widgetType') || attrs.get('type'));
          attrs.delete('__widgetType');
          return [requiredWidget, 'wm-form'];
        }
    };
};


register('wm-form-field', registerFormField.bind(this, true));
register('wm-filter-field', registerFormField.bind(this, false));


export default () => {};
